import Slider from 'react-slick';
import { useState, useEffect, forwardRef, useRef } from 'react';
// @mui
import {
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  Slide,
  Typography,
  Paper,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import DIOText from '../../components/DIOText';
import Image from '../../components/Image';
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
import { CarouselDots } from '../../components/carousel';
// assets
import dynamic from 'next/dynamic';

// Dynamic import DotLottiePlayer to prevent SSR issues
const DotLottiePlayer = dynamic(
  () => import('@dotlottie/react-player').then(mod => mod.DotLottiePlayer),
  { 
    ssr: false,
    loading: () => <div style={{ width: 240, height: 240 }} />
  }
);

if (typeof window !== 'undefined') {
  import('@dotlottie/react-player/dist/index.css');
}

// ----------------------------------------------------------------------

const GUIDES = [
  {
    "id": 1,
    "src": null,
    "img":
      <Image
        disabledEffect
        visibleByDefault
        alt="process-guide-6"
        src="https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/crew-first-work-start-03.png"
        srcSet="
          https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/crew-first-work-start-03.png 1x,
          https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/crew-first-work-start-03-2x.png 2x,
        "
        sx={{ width: 400, height: 240, mx: "auto" }}
      />,
    "imgMo":
      <Image
        disabledEffect
        visibleByDefault
        alt="process-guide-6"
        src="https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/crew-first-work-start-03-mo.png"
        srcSet="
          https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/crew-first-work-start-03-mo.png 1x,
          https://spacey-dio-public.s3.ap-northeast-2.amazonaws.com/images/crew-first-work-start-03-mo-2x.png 2x,
        "
        sx={{ width: 280, height: 168, mx: "auto" }}
      />,
    "line1": [
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
        <SvgIconStyle src={`/icons/icon-pencil-mono.svg`} sx={{ width: 24, height: 24, color: "text.primary" }} />
        <Typography variant="subtitle1">
          출퇴근 시, 오늘 진행할 일과 완료한 일을 기록해야 합니다.
        </Typography>
      </Stack>
    ],
    "line2": [
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <SvgIconStyle src={`/icons/icon-arrow-right-circle-mono.svg`} sx={{ width: 20, height: 20, color: "text.disabled" }} />
        <Typography variant="body2">
          출근 시 진행할 업무를 기록하면 출근 시간이 기록됩니다.
        </Typography>
      </Stack>
    ],
    "line3": [
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <SvgIconStyle src={`/icons/icon-arrow-left-circle-mono.svg`} sx={{ width: 20, height: 20, color: "text.disabled" }} />
        <Typography variant="body2">
          퇴근 시 당일 진행한 업무를 기록하면 퇴근 시간이 기록됩니다.
        </Typography>
      </Stack>
    ],
  },
  {
    "id": 2,
    "src": "/lottie/firston-meeting.lottie",
    "line1": [
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
        <SvgIconStyle src={`/icons/ic_calendar.svg`} sx={{ color: "info.main" }} />
        <Typography variant="subtitle1">
          매주 미팅 일정을 정하세요!
        </Typography>
      </Stack>
    ],
    "line2": [
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <SvgIconStyle src={`/icons/ic_attach_file2.svg`} sx={{ width: 20, height: 20, color: "text.disabled" }} />
        <Typography variant="body2">
          미팅을 통해 회사와 업무 싱크를 맞추고 목표를 정해보세요. <br />
          비동기로 일하는 <DIOText />에서는, 매주 미팅을 하면 효율적인 협업을 할 수 있습니다.
        </Typography>
      </Stack>
    ],
    "line3": [
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Iconify icon={"eva:alert-circle-fill"} sx={{ width: 20, height: 20, color: "text.disabled" }} />
        <Typography variant="body2">
          싱크를 맞추지 않으면 하지 않아도 될 일을 할 수도 있어요.
        </Typography>
      </Stack>
    ],
  },
  {
    "id": 3,
    "src": "/lottie/firston-communicate.lottie",
    "line1": [
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
        <SvgIconStyle src={`/icons/icon-chat-bubble-dots-mono.svg`} sx={{ color: "oldprimary.main" }} />
        <Typography variant="subtitle1">
          기다리지 말고, 메신저로 바로 소통하세요!
        </Typography>
      </Stack>
    ],
    "line2": [
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <SvgIconStyle src={`/icons/icon-loudspeaker-mono.svg`} sx={{ width: 20, height: 20, color: "text.disabled" }} />
        <Typography variant="body2">
          미팅 시간까지 필요한 것을 모아서 요청하지 마세요. <br/>
          필요한 것은 메신저로 바로 요청하세요.
        </Typography>
      </Stack>
    ],
    "line3": [
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Iconify icon={"eva:alert-circle-fill"} sx={{ width: 20, height: 20, color: "text.disabled" }} />
        <Typography variant="body2">
          모호한 일은 빠르게 해소해서 효율적으로 업무해주세요. 
        </Typography>
      </Stack>
    ],
  },
]

const Transition = forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export default function FirstOnDialog({ isOpen, closeDialog }) {
  const smUp = useResponsive('up', 'sm');
  const [currentSlide, setCurrentSlide] = useState(0);

  const carouselRef = useRef(null);

  const settings = {
    dots: true,
    arrows: false,
    autoplay: false,
    infinite: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    rtl: false,
    ...CarouselDots({
      mt: 2,
      rounded: true,
    }),
    beforeChange: (current, next) => setCurrentSlide(next),
  };

  const handleNext = async () => {
    if (currentSlide === GUIDES.length - 1) {
      closeDialog();
    } else {
      carouselRef.current?.slickNext();
    }
  };

  const handlePrevious = async () => {
    carouselRef.current?.slickPrev();
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentSlide(0);
    }
  }, [isOpen]);

  return (
    <Dialog maxWidth="sm" fullScreen={!smUp} fullWidth open={isOpen} TransitionComponent={Transition}>
      <DialogContent sx={{ pb: 0, px: smUp ? 4 : 3, pt: 4 }}>
        <Stack direction="row" alignItems="center" spacing={.5} sx={{ mb: 1 }}>
          <Iconify icon={"eva:info-fill"} sx={{ minWidth: 24, width: 24, height: 24, color: 'text.disabled' }} />
          <Typography variant="subtitle2" color="text.disabled">
            <DIOText /> 첫 출근 업무 가이드
          </Typography>
        </Stack>
        <Typography variant="h5" paragraph>
          🎉 첫 출근 하셨네요!<br/>
          DIO 크루는 이렇게 일해요.
        </Typography>
        <Paper>
          <Slider ref={carouselRef} {...settings}>
            {GUIDES.map((item) => (
              <CarouselItem key={item.id} item={item} />
            ))}
          </Slider>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={2} sx={{ width: 1, justifyContent: "flex-end" }}>
          {currentSlide !== 0 && (
          <LoadingButton
            size="large"
            onClick={handlePrevious}
            sx={{ px: 4 }}
          >
            이전으로
          </LoadingButton>
          )}
          <LoadingButton
            size="large"
            variant="contained"
            onClick={handleNext}
            sx={{ px: 8 - ( 2.5 * currentSlide ) }}
          >
            {currentSlide === GUIDES.length - 1 ? '확인했어요' : '다음'}
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}


function CarouselItem({ item }) {
  const smUp = useResponsive('up', 'sm');

  return (
    <Paper
      sx={{
        p: 3,
        mt: 2,
        backgroundColor: "grey.200"
       }}
    >
      <Paper
        sx={{ mb: 3 }}
      >
        {item.src && (
        <DotLottiePlayer
          autoplay
          loop
          mode="normal"
          src={item.src}
          style={{ width: smUp ? 400 : 280, height: smUp ? 240 : 168, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
        >
        </DotLottiePlayer>
        )}
        {smUp && item.img}
        {!smUp && item.imgMo}
      </Paper>
      <Stack spacing={1}>
        <Stack>
          {item.line1?.map((t) => t)}
        </Stack>
        <Stack>
          {item.line2?.map((t) => t)}
        </Stack>
        <Stack>
          {item.line3?.map((t) => t)}
        </Stack>
      </Stack>
    </Paper>
  )
}